/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/loadGoogleMaps";
import { Loader2 } from "lucide-react";

type Props = {
  /** Optional fixed center. Falls back to the device GPS position. */
  lat?: number | null;
  lng?: number | null;
  /** Continuously track and plot the device's GPS position. */
  track?: boolean;
  height?: number;
};

export default function GpsMap({ lat, lng, track = true, height = 320 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const meMarkerRef = useRef<google.maps.Marker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    loadGoogleMaps()
      .then((g) => {
        if (cancelled || !containerRef.current) return;
        mapRef.current = new g.maps.Map(containerRef.current, {
          center: { lat: lat ?? 39.7392, lng: lng ?? -104.9903 },
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });
        setReady(true);
      })
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  useEffect(() => {
    if (!ready || !track || !navigator.geolocation) return;
    const g = (window as any).google as typeof google;

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (!mapRef.current) return;
        if (!meMarkerRef.current) {
          meMarkerRef.current = new g.maps.Marker({
            position: p,
            map: mapRef.current,
            title: "You",
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: "#2563eb",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 3,
            },
          });
          mapRef.current.setCenter(p);
        } else {
          meMarkerRef.current.setPosition(p);
        }
      },
      (e) => setError(e.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [ready, track]);

  return (
    <div className="relative overflow-hidden rounded-2xl border" style={{ height }}>
      <div ref={containerRef} className="absolute inset-0 bg-muted" />
      {!ready && !error && (
        <div className="absolute inset-0 grid place-items-center bg-muted/60">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 grid place-items-center p-4 text-center text-xs text-muted-foreground">
          Map unavailable: {error}
        </div>
      )}
    </div>
  );
}
